import { FormPageHeader } from 'components/Shared';
import type { ListedArea } from 'contracts/Areas';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactSelect, { MultiValue } from 'react-select';
import { api, apiErrorHandler, notify } from 'services';
import type { AppDispatch, RootState } from 'store';
import { ListAreasActions } from 'store/ducks/areas';
import { UpdateWarehouseAreasActions } from 'store/ducks/warehouseAreas';
import * as S from './styles';

interface Props {
  warehouseId: string | number;
}

interface AreaOption {
  value: number;
  label: string;
}

const areaPath = (warehouseId: string | number) =>
  `warehouses/${warehouseId}/areas`;

const toOption = ({ id, name }: ListedArea): AreaOption => ({
  value: id,
  label: name,
});

export const WarehouseAreasManager = ({ warehouseId }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const requestSequence = useRef(0);
  const { data: areas, loading: loadingAreas } = useSelector(
    (state: RootState) => state.listAreas
  );
  const { loading: saving } = useSelector(
    (state: RootState) => state.updateWarehouseAreas
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [persistedIds, setPersistedIds] = useState<number[]>([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  const options = useMemo(() => areas.map(toOption), [areas]);
  const selected = useMemo(
    () => options.filter(({ value }) => selectedIds.includes(value)),
    [options, selectedIds]
  );
  const hasChanges = useMemo(
    () =>
      selectedIds.length !== persistedIds.length ||
      selectedIds.some((id) => !persistedIds.includes(id)),
    [persistedIds, selectedIds]
  );

  const load = useCallback(async (): Promise<void> => {
    const sequence = ++requestSequence.current;
    dispatch(ListAreasActions.request({ dockManagementOnly: 1 }));
    setIsLoadingLinks(true);
    setLoadFailed(false);
    try {
      const { data } = await api.get<ListedArea[]>(areaPath(warehouseId));
      if (sequence !== requestSequence.current) return;
      const ids = data.map(({ id }) => id);
      setSelectedIds(ids);
      setPersistedIds(ids);
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setLoadFailed(true);
      const { errorMessage } = apiErrorHandler(error);
      notify('error', errorMessage);
    } finally {
      if (sequence === requestSequence.current) setIsLoadingLinks(false);
    }
  }, [dispatch, warehouseId]);

  const onChange = useCallback((values: MultiValue<AreaOption>): void => {
    setSelectedIds(values.map(({ value }) => value));
  }, []);

  const save = useCallback((): void => {
    const nextIds = [...selectedIds];
    dispatch(
      UpdateWarehouseAreasActions.request(
        warehouseId,
        nextIds,
        () => setPersistedIds(nextIds),
        () => setSelectedIds(persistedIds)
      )
    );
  }, [dispatch, persistedIds, selectedIds, warehouseId]);

  useEffect(() => {
    load();
    return () => {
      requestSequence.current += 1;
    };
  }, [load]);

  useEffect(
    () => () => {
      dispatch(ListAreasActions.reset());
      dispatch(UpdateWarehouseAreasActions.reset());
    },
    [dispatch]
  );

  const disabled = isLoadingLinks || loadFailed || saving;

  return (
    <S.MainPanel>
      <FormPageHeader
        title="Áreas do armazém"
        icon={<S.AreaIcon />}
        isLoading={saving || isLoadingLinks}
        actions={
          <S.Button
            size="small"
            type="button"
            disabled={disabled || !hasChanges}
            onClick={save}
          >
            {saving ? <S.ActivityIndicator /> : 'Salvar áreas'}
          </S.Button>
        }
      />
      <S.FormRow>
        <ReactSelect<AreaOption, true>
          inputId="warehouse-areas"
          aria-label="Áreas vinculadas ao armazém"
          placeholder={
            loadFailed
              ? 'Não foi possível carregar as áreas'
              : 'Selecione as áreas deste armazém'
          }
          options={options}
          value={selected}
          onChange={onChange}
          isLoading={loadingAreas || isLoadingLinks}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          styles={S.AreaSelectStyle}
        />
      </S.FormRow>
    </S.MainPanel>
  );
};
